import { useCallback, useMemo, useRef, useState } from "react";

import {
  faChevronDown,
  faFiles,
  faUpload,
} from "@fortawesome/pro-regular-svg-icons";
import { times } from "lodash-es";
import Dropzone, { DropzoneRef, FileRejection } from "react-dropzone";

import { Button } from "src/base-components/Button";
import { OptionsDropdown } from "src/base-components/OptionsDropdown/OptionsDropdown";
import { type OptionsDropdownElement } from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import {
  FileCategory,
  type FileCategoryData,
} from "src/cases/caseView/FileCategory";
import {
  FILE_OPERATIONS,
  useFileCategoryOperation,
} from "src/cases/queries/cases";
import { CaseDeserialized } from "src/cases/queries/casesTypes";
import { UploadFileEnvFilesPostEnvEnum } from "src/clients/files";
import { Accordion } from "src/design-system/Accordion";
import { EmptyState } from "src/design-system/EmptyState";
import { Icon } from "src/design-system/Icon";
import { Skeleton } from "src/design-system/Skeleton";
import { toastActions } from "src/design-system/Toast/utils";
import { useCapabilities } from "src/hooks/useCapabilities";
import { type Environment } from "src/hooks/useEnvironment";
import { useCurrentUserId } from "src/store/AuthStore";
import {
  CASE_FILE_ACCEPTED_FORMATS,
  DEFAULT_ACCEPTED_FILE_TYPES,
  FileSource,
  getCaseFileUploadErrorMessage,
  MAX_FILE_SIZE_BYTES,
} from "src/utils/files";

type CaseFilesProps = {
  caseData?: CaseDeserialized;
  isLoading: boolean;
};

export const CaseFiles: React.FC<CaseFilesProps> = ({
  caseData,
  isLoading,
}) => {
  const fileCategoryOperation = useFileCategoryOperation();
  const currentUserId = useCurrentUserId();
  const { caseFiles, cases } = useCapabilities();
  const dropzoneRef = useRef<DropzoneRef>(null);

  const [isUploading, setIsUploading] = useState(false);
  const [pendingCategory, setPendingCategory] = useState<string | null>(null);
  const [newlyUploadedFileIds, setNewlyUploadedFileIds] = useState<string[]>(
    [],
  );

  const canEditFiles = !!(
    caseData?.data &&
    currentUserId &&
    (cases.canManage ||
      (caseFiles.canEdit && caseData.data.assignee?.id === currentUserId))
  );

  const fileCategories = useMemo((): FileCategoryData[] => {
    if (!caseData?.data.file_categories) return [];

    return caseData.data.file_categories.map((category) => ({
      id: category.id,
      display_name: category.display_name,
      description: category.description || "",
      category_key: category.category_key,
      file_ids: category.file_ids || [],
    }));
  }, [caseData?.data.file_categories]);

  const currentFileIds = useMemo(
    () =>
      Object.fromEntries(
        fileCategories.map((cat) => [cat.category_key, cat.file_ids]),
      ),
    [fileCategories],
  );

  const handleCategorySelect = useCallback((categoryKey: string) => {
    setPendingCategory(categoryKey);
    dropzoneRef.current?.open();
  }, []);

  const dropdownElements = useMemo(
    (): OptionsDropdownElement[] => [
      {
        key: "header",
        header: "Add new file to:",
      },
      ...fileCategories.map((cat) => ({
        key: cat.display_name,
        action: () => handleCategorySelect(cat.category_key),
      })),
    ],
    [fileCategories, handleCategorySelect],
  );

  const env: Environment = caseData?.data.env ?? "live";

  const handleFileUploadWithCategory = useCallback(
    async (file: File, categoryKey: string) => {
      if (!caseData?.data.id) return;

      setIsUploading(true);

      fileCategoryOperation.mutate(
        {
          type: FILE_OPERATIONS.UPLOAD,
          caseId: caseData.data.id,
          file,
          categoryKey,
          env: env as UploadFileEnvFilesPostEnvEnum,
          source: "case-files" as FileSource,
          currentFileIds,
        },
        {
          onSuccess: (data) => {
            if ("fileId" in data && data.fileId) {
              setNewlyUploadedFileIds((prev) => [...prev, data.fileId]);
            }
          },
          onSettled: () => {
            setIsUploading(false);
            setPendingCategory(null);
          },
        },
      );
    },
    [caseData?.data.id, env, currentFileIds, fileCategoryOperation],
  );

  const handleFileSelect = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (acceptedFiles.length > 0 && pendingCategory) {
        handleFileUploadWithCategory(acceptedFiles[0], pendingCategory);
        return;
      }

      if (rejectedFiles.length > 0) {
        const firstRejection = rejectedFiles[0];
        const errorCode = firstRejection.errors[0]?.code;

        toastActions.failure({
          title: getCaseFileUploadErrorMessage(
            errorCode,
            firstRejection.file.name,
          ),
        });
      }
    },
    [pendingCategory, handleFileUploadWithCategory],
  );

  const removeUploadedFileId = useCallback((fileId: string) => {
    setNewlyUploadedFileIds((prev) => prev.filter((id) => id !== fileId));
  }, []);

  const handleDeleteFile = useCallback(
    (fileId: string, fileName: string, categoryKey: string) => {
      if (!caseData?.data.id) return;

      fileCategoryOperation.mutate(
        {
          type: FILE_OPERATIONS.DELETE,
          caseId: caseData.data.id,
          fileId,
          fileName,
          categoryKey,
          currentFileIds,
        },
        {
          onSuccess: () => removeUploadedFileId(fileId),
        },
      );
    },
    [
      caseData?.data.id,
      currentFileIds,
      fileCategoryOperation,
      removeUploadedFileId,
    ],
  );

  const handleMoveFile = useCallback(
    (
      fileId: string,
      fileName: string,
      fromCategory: string,
      toCategory: string,
    ) => {
      if (!caseData?.data.id) return;

      fileCategoryOperation.mutate({
        type: FILE_OPERATIONS.MOVE,
        caseId: caseData.data.id,
        fileId,
        fileName,
        fromCategoryKey: fromCategory,
        toCategoryKey: toCategory,
        currentFileIds,
      });
    },
    [caseData?.data.id, currentFileIds, fileCategoryOperation],
  );

  const categoriesWithFiles = fileCategories
    .filter((cat) => cat.file_ids.length > 0)
    .map((cat) => cat.category_key);

  if (isLoading) {
    return (
      <div className="flex h-full flex-col p-4">
        <div className="border-gray-200 border-b bg-white pb-4">
          <div className="rounded-lg border border-gray-200 border-dashed bg-white p-4">
            <div className="flex gap-4">
              <div className="shrink-0">
                <Skeleton height="h-12" rounded="rounded-lg" width="w-12" />
              </div>
              <div className="flex-1 space-y-2">
                <Skeleton height="h-4" width="w-48" />
                <Skeleton height="h-3" width="w-64" />
                <div className="mt-3">
                  <Skeleton height="h-8" width="w-24" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto pt-4">
          <div className="divide-y divide-gray-200 rounded-lg border border-gray-200">
            {times(3, (i) => (
              <div key={i} className="p-4">
                <div className="flex items-center gap-2">
                  <Skeleton height="h-4" width="w-24" />
                  <Skeleton height="h-5" rounded="rounded-full" width="w-12" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!caseData) {
    return (
      <EmptyState
        description="Case files are not available for this case"
        headline="No case data available"
        icon={faFiles}
        variant="default"
      />
    );
  }

  const buttonDisabled = !canEditFiles || isUploading;

  return (
    <div className="flex h-full flex-col p-4">
      <div className="border-gray-200 border-b bg-white pb-4">
        <div className="rounded-lg border border-gray-200 border-dashed bg-white p-4">
          <div className="flex gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-indigo-100">
              <Icon icon={faUpload} size="md" variant="active" />
            </div>
            <div className="flex-1">
              <h3 className="font-inter-semibold-13px text-gray-800">
                Click here to upload
              </h3>
              <p className="mt-1 font-inter-normal-12px text-gray-500">
                {canEditFiles
                  ? `Supported types: ${CASE_FILE_ACCEPTED_FORMATS.join(", ")}`
                  : "You do not have permission to upload files"}
              </p>
              <div className="mt-3 flex justify-start">
                <OptionsDropdown
                  buttonDataLoc="upload-file-dropdown"
                  elements={dropdownElements}
                  placement="bottom start"
                  disabled={buttonDisabled}
                >
                  <Button
                    size="sm"
                    variant="secondary"
                    iconRight={faChevronDown}
                    disabled={buttonDisabled}
                  >
                    {isUploading ? "Uploading..." : "Upload a file"}
                  </Button>
                </OptionsDropdown>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto pt-4">
        {fileCategories.length > 0 ? (
          <Accordion defaultValue={categoriesWithFiles}>
            {fileCategories.map((category) => (
              <FileCategory
                key={category.category_key}
                canDelete={canEditFiles}
                canEdit={canEditFiles}
                category={category}
                env={env}
                fileCategories={fileCategories}
                newlyUploadedFileIds={newlyUploadedFileIds}
                onDeleteFile={handleDeleteFile}
                onMoveFile={handleMoveFile}
              />
            ))}
          </Accordion>
        ) : (
          <EmptyState
            description="No file categories have been configured for this case template"
            headline="No file categories"
            icon={faFiles}
            variant="default"
          />
        )}
      </div>

      <div className="hidden">
        <Dropzone
          ref={dropzoneRef}
          accept={DEFAULT_ACCEPTED_FILE_TYPES}
          disabled={!canEditFiles || isUploading}
          maxFiles={1}
          maxSize={MAX_FILE_SIZE_BYTES}
          noClick
          noDrag
          onDrop={handleFileSelect}
        >
          {({ getRootProps, getInputProps }) => (
            <div {...getRootProps()}>
              <input {...getInputProps()} />
            </div>
          )}
        </Dropzone>
      </div>
    </div>
  );
};
